<template>
  <div class="flex justify-between border-gray-200">
    <h1 class="font-semibold text-3xl mb-1">Cancellations</h1>
  </div>

  <div class="bg-white border border-gray-200 rounded-lg mt-4">
    <CancellationsControls
      @update:date-range="onDateRangeChange"
      @update:clear-filters="onClearFilters"
      @update:search-term="onSearchTermChange"
    />
    <DataTable
      :is-loading="cancellationsStore.loading"
      :columns="cancellationsTableColumns"
      :data="paginatedCancellations || []"
      :total-count="totalCount"
      :page-number="pageNumber"
      :page-size="pageSize"
      @update:page="onUpdatePage"
      @update:page-size="onUpdatePageSize"
    />
  </div>
</template>

<script setup lang="ts">
import type { OrderServiceGetManyRequest } from '@/models/orders';
import { useCancellationStore } from '@/stores/cancellations';
import { addMetric } from '@/utils/monitoring.utils';
import { deepEqual } from '@/utils/paging.utils';
import type { DateRange } from 'radix-vue';
import { computed, onMounted, ref, watch } from 'vue';
import DataTable from '../../DataTable.vue';
import CancellationsControls from '../cancellations-controls/CancellationsControls.vue';
import { cancellationsTableColumns } from './CancellationsTableColumns';

const cancellationsStore = useCancellationStore();

const cancellationsParams = ref<OrderServiceGetManyRequest>({
  populate: ['plans', 'plans.payments', 'plans.payments.connectorData', 'plans.benefactor', 'supplier'],
});
const previousParams = ref<OrderServiceGetManyRequest>({ ...cancellationsParams.value });

const paginatedCancellations = computed(() => {
  return cancellationsStore.unresolvedCancellations?.slice(
    (pageNumber.value - 1) * pageSize.value,
    pageNumber.value * pageSize.value,
  );
});

const totalCount = ref(1);
const pageNumber = ref(1);
const pageSize = ref(10);

onMounted(async () => onUpdateFilter(cancellationsParams.value, true));

const onUpdateFilter = async (params: OrderServiceGetManyRequest, skipCheck: boolean = false) => {
  if (!skipCheck) {
    if (deepEqual(params, previousParams.value)) {
      return;
    }
    previousParams.value = { ...params };
  }

  cancellationsStore.fetchUnresolvedCancellations(params);
};

const onDateRangeChange = (value: DateRange): void => {
  cancellationsParams.value.from = `${new Date(`${value.start?.year}-${value.start?.month}-${value.start?.day}`)}`;
  cancellationsParams.value.to = `${new Date(`${value.end?.year}-${value.end?.month}-${value.end?.day}`)}`;
  onUpdateFilter(cancellationsParams.value);
};

const onClearFilters = (): void => {
  cancellationsParams.value = { limit: 500 };
  onUpdateFilter(cancellationsParams.value);
};

const onSearchTermChange = (value: string): void => {
  cancellationsParams.value.search = value.toUpperCase();
  onUpdateFilter(cancellationsParams.value);
};

const onUpdatePage = (pageNo: number) => {
  pageNumber.value = pageNo;
};

const onUpdatePageSize = (size: number) => {
  pageSize.value = size;
};

watch(
  () => cancellationsStore.unresolvedCancellations,
  (unresolvedCancellations) => {
    if (!unresolvedCancellations) return;

    totalCount.value = unresolvedCancellations.length;
    pageNumber.value = 1;
    addMetric('cancellations-table', { status: 'success', channel: 'merchant', method: 'fetchOrders' });
  },
);
</script>
